import { useMemo, useState } from "react";
import { BarChart2, RefreshCw, ArrowRight } from "lucide-react";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

import { getAnalytics, type Building } from "@/lib/api/building";
import { useScopedFindings } from "@/lib/api/useScopedFindings";
import type { WidgetSpec } from "@/lib/api/agent";
import { WidgetRenderer } from "@/components/chat/WidgetRenderer";
import { FindingsList } from "@/components/building/findings/FindingsList";
import {
  energy_kpi_items,
  templates_for_scope,
  widgets_for_template,
  type ReportScopeLevel,
  type ReportTemplateId,
} from "./report-templates";

const TIME_CHIPS = [
  { days: 7 as const, label: "7 pv" },
  { days: 30 as const, label: "30 pv" },
  { days: 90 as const, label: "90 pv" },
];

interface Props {
  buildingId: string | null;
  candidateBuildings?: Building[];
}

export function SmartReport({ buildingId, candidateBuildings = [] }: Props) {
  // Salkku and kanta share the Q2 template set (Energia-analyysi / Kiinteistövertailu /
  // Toimintahistoria). The route already resolved the scoped building pool.
  const scope_level: ReportScopeLevel = buildingId ? "building" : "group";
  const templates = templates_for_scope(scope_level);
  const building_ids = useMemo(
    () => (buildingId ? [buildingId] : candidateBuildings.map((b) => b.id)),
    [buildingId, candidateBuildings],
  );
  // Portfolio Toimintahistoria tab: same shared load path as left-nav Havainnot
  // (useScopedFindings) — `candidateBuildings` is already resolved from the same URL scope
  // this hook reads, so this stays in sync without a second building_ids fan-out.
  const {
    findings: portfolioFindings,
    loading: portfolioFindingsLoading,
    onAcknowledged: patchAcknowledgedPortfolioFinding,
  } = useScopedFindings();

  const [selected_template, set_selected_template] =
    useState<ReportTemplateId | null>(null);
  const [time_range, set_time_range] = useState<7 | 30 | 90>(30);
  const [widgets, set_widgets] = useState<WidgetSpec[]>([]);
  const [generated, set_generated] = useState(false);
  const [loading, set_loading] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const scope_label = buildingId
    ? "Rakennustason raportti"
    : "Kiinteistösalkun / kannan analyysi";

  function handle_reset() {
    set_widgets([]);
    set_generated(false);
    set_error(null);
  }

  async function submit() {
    if (!selected_template || loading) return;
    set_error(null);
    set_loading(true);
    try {
      let kpi: ReturnType<typeof energy_kpi_items> | undefined;
      if (selected_template === "energy" && building_ids.length > 0) {
        const snapshots = await Promise.all(
          building_ids.map((id) =>
            getAnalytics(id, time_range).catch(() => null),
          ),
        );
        const ok = snapshots.filter(
          (snap): snap is NonNullable<typeof snap> => snap !== null,
        );
        if (ok.length > 0) kpi = energy_kpi_items(ok);
      }
      set_widgets(
        widgets_for_template({
          template_id: selected_template,
          level: scope_level,
          building_id: buildingId,
          building_ids,
          days: time_range,
          kpi,
        }),
      );
      set_generated(true);
    } catch (e) {
      set_error(e instanceof Error ? e.message : "Virhe");
    } finally {
      set_loading(false);
    }
  }

  const show_findings =
    generated && selected_template === "history" && building_ids.length > 0;

  return (
    <div
      className={cn("flex flex-col", cardBase, "overflow-hidden min-h-[600px]")}
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
        <BarChart2 className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Kiinteistöraportti</div>
          <div className="text-[11px] text-muted-foreground">{scope_label}</div>
        </div>
        {generated && (
          <button
            type="button"
            onClick={handle_reset}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition px-2 py-1 rounded-md hover:bg-surface-elevated"
          >
            <RefreshCw className="h-3 w-3" />
            Luo uusi raportti
          </button>
        )}
      </header>

      {!generated && (
        <div className="px-4 py-4 border-b border-border bg-surface-elevated/30 space-y-3 shrink-0">
          <div className="flex flex-wrap gap-2">
            {templates.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => set_selected_template(type.id)}
                className={cn(
                  "text-[12px] px-3 py-1.5 rounded-full border transition",
                  selected_template === type.id
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border bg-surface text-foreground/80 hover:border-primary/40 hover:text-foreground hover:bg-surface-elevated",
                )}
              >
                {type.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              {TIME_CHIPS.map(({ days, label }) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => set_time_range(days)}
                  className={cn(
                    "text-[11px] px-2.5 py-1 rounded-md border transition",
                    time_range === days
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-surface text-muted-foreground hover:border-primary/40 hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={loading || !selected_template}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ArrowRight className="h-4 w-4" />
              Luo raportti
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="mx-4 mt-3 text-[11px] text-destructive border border-destructive/20 rounded-md px-3 py-1.5 bg-destructive/5 shrink-0">
          {error}
        </p>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
        {!generated ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
            <BarChart2 className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Valitse malli ja jakso, sitten Luo raportti
            </p>
          </div>
        ) : show_findings ? (
          buildingId ? (
            <FindingsList
              buildingId={buildingId}
              defaultStatuses={["open"]}
              showStatusFilter={false}
              title="Toimintahistoria"
              emptyMessage="Ei avoimia havaintoja valitussa laajuudessa."
            />
          ) : (
            <FindingsList
              findings={portfolioFindings ?? []}
              loading={portfolioFindingsLoading}
              onAcknowledged={patchAcknowledgedPortfolioFinding}
              defaultStatuses={["open"]}
              showStatusFilter={false}
              title="Toimintahistoria"
              emptyMessage="Ei avoimia havaintoja valitussa laajuudessa."
            />
          )
        ) : widgets.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-muted-foreground">
              Ei kiinteistöjä valitussa laajuudessa.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {widgets.map((w, i) => (
              <WidgetRenderer
                key={i}
                widget={w}
                buildingId={buildingId ?? undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
